import type Store from '@ember-data/store'
import Service, { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import type DashboardPreferences from 'teacher-ui-client/models/dashboard-preferences'
import type SessionService from 'teacher-ui-client/services/session'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import {
  WIDGET_IDS,
  buildTeacherHomeWidgetViewModels,
  cloneTeacherHomePreferences,
  normalizeTeacherHomePreferences,
  type TeacherHomePreferences,
  type TeacherHomeWidgetId,
} from 'teacher-ui-client/teacher-home/widget-registry'

function buildPreferencesFromIds(
  teacherHomeEnabled: boolean,
  enabledIds: string[],
  disabledIds: TeacherHomeWidgetId[],
) {
  return normalizeTeacherHomePreferences({
    teacherHomeEnabled,
    widgets: [...enabledIds, ...disabledIds].map((id, index) => ({
      id,
      enabled: index < enabledIds.length,
    })),
  })
}

export default class DashboardPreferencesService extends Service {
  @service session!: SessionService
  @service store!: Store
  @service('subscription-type') subscriptionType!: SubscriptionTypeService

  @tracked draftPreferences?: TeacherHomePreferences

  get teacherHomeOptedIn() {
    return this.session.currentTeacher?.dashboardPreferences?.preferences.teacher_home_enabled ?? false
  }

  get hasPersistedTeacherHomePreference() {
    return Boolean(this.session.currentTeacher?.dashboardPreferences)
  }

  get teacherHomeEnabled() {
    return this.persistedPreferences.teacherHomeEnabled
  }

  get orderedEnabledWidgets() {
    return this.toWidgetViewModels(this.persistedPreferences).filter((widget) => widget.enabled)
  }

  get allWidgets() {
    return this.toWidgetViewModels(this.persistedPreferences)
  }

  get enabledDraftWidgets() {
    return this.toWidgetViewModels(this.draftPreferences ?? this.persistedPreferences).filter(
      (widget) => widget.enabled,
    )
  }

  get availableDraftWidgets() {
    return this.toWidgetViewModels(this.draftPreferences ?? this.persistedPreferences)
      .filter((widget) => !widget.enabled)
      .sort((left, right) => left.label.localeCompare(right.label))
  }

  get persistedPreferences() {
    return this.loadPreferences()
  }

  beginEditing() {
    this.draftPreferences = cloneTeacherHomePreferences(this.persistedPreferences)
  }

  cancelEditing() {
    this.draftPreferences = undefined
  }

  reorderDraft(orderedIds: TeacherHomeWidgetId[]) {
    const draftPreferences = this.requireDraftPreferences()
    // Preferences are persisted globally, so only the widgets visible for the current
    // subscription are reordered here. Hidden subscription-specific widgets stay in place.
    const currentVisibleEnabledIds = this.toWidgetViewModels(draftPreferences)
      .filter((widget) => widget.enabled)
      .map(({ id }) => id)
    // We need both the current visible ids and their reordered version so we can replace
    // only that subset in the persisted global order while leaving hidden widgets untouched.
    const reorderedVisibleEnabledIds = orderedIds.concat(
      currentVisibleEnabledIds.filter((id) => !orderedIds.includes(id)),
    )
    const nextOrderedIds = this.mergeDraftOrder(draftPreferences, currentVisibleEnabledIds, reorderedVisibleEnabledIds)
    const widgetStatesById = new Map(draftPreferences.widgets.map((widget) => [widget.id, widget.enabled]))

    this.draftPreferences = {
      teacherHomeEnabled: draftPreferences.teacherHomeEnabled,
      widgets: nextOrderedIds.map((id) => ({
        id,
        enabled: widgetStatesById.get(id) ?? true,
      })),
    }
  }

  setDraftWidgetEnabled(id: TeacherHomeWidgetId, enabled: boolean) {
    const draftPreferences = this.requireDraftPreferences()
    const enabledIds = draftPreferences.widgets.filter((widget) => widget.enabled).map(({ id: widgetId }) => widgetId)
    const disabledIds = draftPreferences.widgets.filter((widget) => !widget.enabled).map(({ id: widgetId }) => widgetId)

    if (enabled) {
      this.draftPreferences = buildPreferencesFromIds(
        draftPreferences.teacherHomeEnabled,
        enabledIds.includes(id) ? enabledIds : [...enabledIds, id],
        disabledIds.filter((widgetId) => widgetId !== id),
      )
      return
    }

    this.draftPreferences = buildPreferencesFromIds(
      draftPreferences.teacherHomeEnabled,
      enabledIds.filter((widgetId) => widgetId !== id),
      disabledIds.includes(id) ? disabledIds : [...disabledIds, id],
    )
  }

  async save() {
    const draftPreferences = this.requireDraftPreferences()

    await this.persistPreferences(draftPreferences)
    this.draftPreferences = undefined
  }

  async setTeacherHomeEnabled(enabled: boolean) {
    const nextPreferences = cloneTeacherHomePreferences(this.persistedPreferences)
    nextPreferences.teacherHomeEnabled = enabled

    await this.persistPreferences(nextPreferences)

    if (this.draftPreferences) {
      this.draftPreferences = cloneTeacherHomePreferences(nextPreferences)
    }
  }

  private loadPreferences() {
    const storedPreferences = this.session.currentTeacher?.dashboardPreferences?.preferences

    if (!storedPreferences) {
      return normalizeTeacherHomePreferences()
    }

    const enabledWidgetIds = Array.isArray(storedPreferences.widgets) ? storedPreferences.widgets : []

    return buildPreferencesFromIds(
      storedPreferences.teacher_home_enabled,
      enabledWidgetIds,
      WIDGET_IDS.filter((id) => !enabledWidgetIds.includes(id)),
    )
  }

  private async persistPreferences(preferences: TeacherHomePreferences) {
    const teacher = this.session.currentTeacher
    const serializedPreferences = {
      teacher_home_enabled: preferences.teacherHomeEnabled,
      widgets: preferences.widgets.filter((widget) => widget.enabled).map(({ id }) => id),
    }

    let dashboardPreferences = teacher.dashboardPreferences

    if (!dashboardPreferences) {
      dashboardPreferences = this.store.createRecord<DashboardPreferences>('dashboard-preferences', {
        teacher,
        preferences: serializedPreferences,
      })
      teacher.dashboardPreferences = dashboardPreferences
    } else {
      dashboardPreferences.preferences = serializedPreferences
    }

    await dashboardPreferences.save()
  }

  private requireDraftPreferences() {
    if (!this.draftPreferences) {
      throw new Error('Dashboard preferences draft requested before beginEditing()')
    }

    return this.draftPreferences
  }

  private toWidgetViewModels(preferences: TeacherHomePreferences) {
    // Subscription-specific visibility is applied when building view models so saves from
    // another subscription do not drop widgets that are merely hidden in that context.
    return buildTeacherHomeWidgetViewModels(preferences, this.subscriptionType.currentValue)
  }

  private mergeDraftOrder(
    preferences: TeacherHomePreferences,
    currentVisibleEnabledIds: TeacherHomeWidgetId[],
    reorderedVisibleEnabledIds: TeacherHomeWidgetId[],
  ) {
    const visibleIdSet = new Set(currentVisibleEnabledIds)
    const remainingVisibleIds = reorderedVisibleEnabledIds.slice()

    return preferences.widgets.map(({ id, enabled }) => {
      if (!enabled || !visibleIdSet.has(id)) {
        return id
      }

      return remainingVisibleIds.shift() ?? id
    })
  }
}

declare module '@ember/service' {
  interface Registry {
    'dashboard-preferences': DashboardPreferencesService
  }
}
